import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import { getDetailedProductVariantStock } from '@ancon/wildcat-utils/inventory'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import isDetailedProductVariantOutOfStock from '@ancon/wildcat-utils/inventory/isDetailedProductVariantOutOfStocks'

import ProductConfigurationAccordion from '../components/ProductConfigurationAccordion'
import ProductSelection from '../components/ProductSelection'
import useAppSelector from '../../../store/hooks/useAppSelector'
import useAppStore from '../../../store/hooks/useAppStore'
import { productsDetailsVariantsSelector } from '../store/productSelectors'
import { outletSelectedOutletIdSelector } from '../../outlet/store/outletSelector'
import { SelectableType } from '../../app/components/Selectable'
import { ConfigureProductFormState } from '../types'
import { ProductConfigureAccordionId } from '../constants'
import { checkoutItemQuantityByVariantIdSelector } from '../../checkout/store/checkoutSelectors'

export default function ProductVariantsForm() {
  const { t } = useTranslation('common')

  const store = useAppStore()

  const { watch, setValue, trigger, formState } =
    useFormContext<ConfigureProductFormState>()

  const { errors } = formState

  const [activeAccordionId, selectedVariantId, quantity] = watch([
    'activeAccordionId',
    'selectedVariantId',
    'quantity',
  ])

  const {
    field: { onChange },
  } = useController({
    name: 'selectedVariantId',
    rules: {
      required: t('components.productDetailsModal.mandatoryChoice'),
    },
  })

  const outletId = useAppSelector(outletSelectedOutletIdSelector)
  const variants = useAppSelector(productsDetailsVariantsSelector)!

  const variantsError = errors.selectedVariantId

  useEffect(() => {
    // Trigger initial validation cycle
    trigger('selectedVariantId')
  }, [trigger])

  function handleVariantChange(newVariantId: string) {
    const selectedVariant = variants.find(v => v.id === newVariantId)

    if (selectedVariant) {
      const variantStock = getDetailedProductVariantStock(
        selectedVariant,
        outletId!,
      )

      const alreadyInCartCount = checkoutItemQuantityByVariantIdSelector(
        store.getState(),
        newVariantId,
      )

      // Set quantity to max available stock if it's greater than the current quantity
      if (variantStock?.stockCount) {
        const maxEligibleQuantity = Math.max(
          variantStock.stockCount - alreadyInCartCount,
          0,
        )

        if (quantity > maxEligibleQuantity) {
          setValue('quantity', maxEligibleQuantity)
        }
      }

      setValue('selectedVariantId', selectedVariant.id)
      setValue(
        'selectedVariantShowingPrice',
        selectedVariant.variantPrice.unitPrice,
      )
      onChange(newVariantId)
      // Trigger validation cycle
      trigger('selectedVariantId')
    }
  }

  function handleToggleAccordion(isExpanded: boolean) {
    if (isExpanded) {
      const addonAccordionId = ProductConfigureAccordionId.Variants
      setValue('activeAccordionId', addonAccordionId)
    }
  }

  return (
    <ProductConfigurationAccordion
      title={t('components.productDetailsModal.choiceOfVariant')}
      subtitle={variantsError?.message}
      expanded={activeAccordionId === ProductConfigureAccordionId.Variants}
      onToggle={handleToggleAccordion}
      hasError={!!variantsError}
    >
      {variants.map(variant => {
        const { name, customerFacingName, id, variantPrice } = variant
        const isOutOfStock = isDetailedProductVariantOutOfStock(
          variant,
          outletId!,
        )

        return (
          <ProductSelection
            key={id}
            label={customerFacingName || name}
            priceLabel={
              isOutOfStock
                ? t('components.productDetailsModal.variantOutOfStock')
                : getFormattedCurrency(
                    variantPrice.unitPrice,
                    variantPrice.unitPriceCurrency,
                  )
            }
            disabled={isOutOfStock}
            selectionType={SelectableType.RadioButton}
            selected={selectedVariantId === id}
            onChange={() => handleVariantChange(id)}
          />
        )
      })}
    </ProductConfigurationAccordion>
  )
}
